import React from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { useStaticQuery, graphql } from "gatsby"

/**
 * Optimized image component using GatsbyImage
 * 
 * @param {string} imagePath - Relative path to image in assets/images folder
 * @param {string} alt - Alt text for the image
 * @param {object} style - Optional inline styles
 * @param {string} className - Optional CSS classes
 * @param {boolean} loading - Set to "lazy" by default for improved performance
 */
const OptimizedImage = ({ imagePath, alt, style = {}, className = "", loading = "lazy" }) => {
  const data = useStaticQuery(graphql`
    query {
      allFile(filter: {sourceInstanceName: {eq: "images"}}) {
        edges {
          node {
            relativePath
            childImageSharp {
              gatsbyImageData(
                placeholder: BLURRED, 
                layout: CONSTRAINED,
                formats: [AUTO, WEBP, AVIF]
              )
            }
          }
        }
      }
    }
  `)

  // Find the requested image in the GraphQL result
  const image = data.allFile.edges.find(edge => edge.node.relativePath === imagePath)

  // If no image is found, return a fallback or log an error
  if (!image) {
    console.error(`Image not found: ${imagePath}`)
    // Fallback to regular img tag with original path if available
    return (
      <img 
        src={`/static/${imagePath}`} 
        alt={alt} 
        style={style}
        className={className}
        loading={loading}
      />
    )
  }

  // Get the optimized image data
  const optimizedImage = getImage(image.node)

  // Return the GatsbyImage component
  return (
    <GatsbyImage
      image={optimizedImage}
      alt={alt}
      style={style}
      className={className}
      loading={loading}
    />
  )
}

export default OptimizedImage
